import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Save, RefreshCw, LayoutList, LayoutGrid, Loader2,
  GalleryHorizontal, BrainCircuit, Palette, FileText,
  Film, Wand2, SquareStack, Monitor, Smartphone, Square, Video, Layers,
  Check, ChevronDown, Download, Sparkles, Activity, CheckSquare,
  Clapperboard, Plus, PanelRightOpen, PanelRightClose
} from "lucide-react";
import { useTheme } from "@context/ThemeContext";
import { STYLE_OPTIONS, VIDEO_MODELS, ASPECT_RATIOS } from "./common/Constants";
import SystemMonitor from "../common/SystemMonitor";
import QueueStatusBar from "../common/QueueStatusBar";
import EstimatedPoints from "../common/EstimatedPoints";
import { useDelayedUnmount } from "../../hooks/useDelayedUnmount";
import { getCurrentImageProvider, normalizeWanxImageQualityTier } from "../../utils/wanxImageCapabilities";
import {
  isWanxVideoModel,
  normalizeWanxVideoModel,
} from "../../utils/wanxVideoCapabilities";
import {
  getProjectVideoProvider,
  normalizeVideoProvider,
} from "../../utils/projectRuntime";

// 比例切换按钮组
const RatioToggle = ({ currentRatio, onChange }) => {
  return (
    <div className="flex items-center p-1 rounded-lg gap-1"
      style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
      {ASPECT_RATIOS.map(({ id, icon: Icon }) => (
        <button
          key={id}
          onClick={() => onChange(id)}
          className={`flex items-center justify-center w-8 h-8 rounded-md transition-colors duration-150 cursor-pointer ${currentRatio === id ? '' : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'}`}
          style={currentRatio === id
            ? {
                backgroundColor: 'var(--color-bg-primary)',
                color: 'var(--color-accent-primary)',
                boxShadow: 'var(--shadow-card)'
              }
            : undefined
          }
          title={id}
        >
          <Icon size={14} />
        </button>
      ))}
    </div>
  );
};

// 风格选择下拉
const StyleDropdown = ({ currentStyle, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { mounted, exiting } = useDelayedUnmount(isOpen, 150);
  const { getTheme } = useTheme();
  const theme = getTheme();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentOption = STYLE_OPTIONS.find(opt => opt.value === currentStyle);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
        style={{
          backgroundColor: 'var(--color-bg-overlay)',
          border: '1px solid var(--color-border-primary)',
          color: 'var(--color-text-primary)'
        }}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Palette size={13} style={{ color: theme.colors.accentPrimary }}/>
        <span>{currentOption?.label || '风格'}</span>
        <ChevronDown size={12} style={{ 
          color: 'var(--color-text-muted)',
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
          transition: 'transform 0.2s'
        }}/>
      </button>

      {mounted && (
        <div className={`absolute top-full left-0 mt-1.5 w-44 rounded-xl shadow-2xl py-2 z-50 ${exiting
          ? "animate-out fade-out slide-out-to-top-2 duration-150"
          : "animate-in fade-in slide-in-from-top-2 duration-150"}`}
          style={{ 
            backgroundColor: 'var(--color-bg-secondary)', 
            border: '1px solid var(--color-border-primary)'
          }}>
          {STYLE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
              className="w-full flex items-center justify-between px-3 py-2.5 text-left transition-all duration-200 cursor-pointer hover:bg-[var(--color-bg-tertiary)]"
            >
              <span className="text-xs font-medium" style={{ 
                color: currentStyle === opt.value 
                  ? theme.colors.accentPrimary 
                  : 'var(--color-text-primary)'
              }}>
                {opt.label}
              </span>
              {currentStyle === opt.value && (
                <Check size={14} style={{ color: theme.colors.accentPrimary }}/>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const StoryboardToolbar = ({
  rows,
  currentRatio,
  setCurrentRatio,
  currentStyle,
  setCurrentStyle,
  viewMode,
  setViewMode,
  isRefreshingAssets,
  fetchAssets,
  isSaving,
  handleSaveAll,
  isGlobalLocked,
  onBatchGenerateImages,
  onBatchGenerateVideos,
  onBatchSequentialImages,
  onBatchGenerateSequenceVideos,
  onBatchDownloadVideos,
  onBatchExpandToCanvas,
  isExpandingToCanvas,
  onOpenSourceText,
  batchAction,
  batchProgress,
  onCancelBatch,
  onShowGridWizard,
  // 多选
  isMultiSelectMode,
  setIsMultiSelectMode,
  selectedShots,
  selectAllShots,
  clearShotSelection,
  videoEnabled,
  onVideoEnabledChange,
  projectId,
}) => {
  const rowsCount = rows?.length || 0;
  const { getTheme } = useTheme();
  const theme = getTheme();

  const [showSystemMonitor, setShowSystemMonitor] = useState(false);
  const [showQueuePopover, setShowQueuePopover] = useState(false);
  const queuePopover = useDelayedUnmount(showQueuePopover, 150);

  // 检测是否是首尾帧模式（检查是否有 scene_number 字段）
  const isFirstLastFrameMode = rows?.some(r => r.scene_number || r.sceneNumber) || false;
  const estimateRows = useMemo(() => {
    if (!Array.isArray(rows) || !rows.length) return [];
    if (selectedShots?.size > 0) {
      return rows.filter((_, index) => selectedShots.has(index));
    }
    return rows;
  }, [rows, selectedShots]);
  const estimatePromptCharacters = useMemo(
    () => estimateRows.reduce((total, row) => total + String(
      row?.image_prompt
      || row?.imagePrompt
      || row?.video_prompt
      || row?.videoPrompt
      || row?.visual_content
      || row?.action
      || ""
    ).length, 0),
    [estimateRows]
  );
  const currentImageProvider = getCurrentImageProvider();
  const imageQualityTier = normalizeWanxImageQualityTier(estimateRows[0]?.image_quality_tier
    || estimateRows[0]?.imageQualityTier
    || localStorage.getItem("image_quality_tier")
    || "4K", currentImageProvider);
  const imageCostRequest = estimateRows.length ? {
    type: "image",
    model: currentImageProvider,
    resolution: imageQualityTier,
    count: estimateRows.length * (isFirstLastFrameMode ? 2 : 1),
    promptCharacters: estimatePromptCharacters,
    source: "storyboard_batch_image",
  } : null;
  const firstEstimateRow = estimateRows[0] || {};
  const projectVideoProvider = normalizeVideoProvider(getProjectVideoProvider(projectId, ""));
  const firstRowVideoIdentity = firstEstimateRow?.video_provider
    || firstEstimateRow?.videoProvider
    || firstEstimateRow?.video_model
    || firstEstimateRow?.videoModel
    || "guavideo";
  const isWanxEstimate = firstRowVideoIdentity === "wanx" || isWanxVideoModel(firstRowVideoIdentity);
  const estimateVideoProvider = isWanxEstimate ? "wanx" : firstRowVideoIdentity;
  const totalVideoDuration = estimateRows.reduce((total, row) => {
    const duration = Number(
      (isWanxEstimate ? (row?.video_wan_duration ?? row?.videoWanDuration) : null)
      ?? row?.video_duration
      ?? row?.videoDuration
      ?? row?.duration_seconds
      ?? row?.duration
      ?? 5
    );
    return total + (Number.isFinite(duration) && duration > 0 ? duration : 5);
  }, 0);
  const videoCostRequest = videoEnabled && estimateRows.length ? {
    type: "video",
    model: estimateVideoProvider,
    subModel: isWanxEstimate
        ? normalizeWanxVideoModel(firstEstimateRow?.video_sub_model || firstEstimateRow?.videoSubModel)
        : (firstEstimateRow?.video_sub_model || firstEstimateRow?.videoSubModel || firstEstimateRow?.video_model || ""),
    resolution: isWanxEstimate
        ? (firstEstimateRow?.video_wan_resolution || firstEstimateRow?.videoWanResolution || "1080P")
        : (firstEstimateRow?.video_resolution || firstEstimateRow?.videoResolution || firstEstimateRow?.resolution || "480p"),
    totalDurationSeconds: Math.max(1, Math.round(totalVideoDuration)),
    count: estimateRows.length,
    promptCharacters: estimatePromptCharacters,
    source: "storyboard_batch_video",
  } : null;

  const viewButtons = [
    { mode: 'list', icon: LayoutList, label: '列表' },
    { mode: 'text', icon: FileText, label: '文字' },
    { mode: 'grid', icon: LayoutGrid, label: '网格' },
    { mode: 'gallery', icon: GalleryHorizontal, label: '画廊' },
  ];

  const handleRatioChange = (ratio) => {
    setCurrentRatio(ratio);
    localStorage.setItem("video_ratio", ratio);
  };

  const handleStyleChange = (style) => {
    setCurrentStyle(style);
    localStorage.setItem("gen_style", style);
  };

  return (
    <div className="flex flex-col">
      {/* Main Toolbar — 圆角卡片 */}
      <div className="px-6 pt-4 pb-2" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
        <div
          className="flex items-center justify-between px-4 py-2.5 rounded-xl shrink-0 gap-4"
          style={{
            backgroundColor: 'var(--color-bg-card)',
            border: '1px solid var(--color-border-primary)',
            boxShadow: 'var(--shadow-card)',
          }}
        >
        {/* 左侧：视图切换 */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className="flex items-center gap-1 p-1 rounded-xl"
              style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-secondary)' }}>
              {viewButtons.map(({ mode, icon: Icon, label }) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors duration-150 text-xs font-medium cursor-pointer ${viewMode === mode ? '' : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-tertiary)]'}`}
                style={viewMode === mode
                  ? {
                      backgroundColor: 'var(--color-bg-primary)',
                      color: theme.colors.accentPrimary,
                      boxShadow: 'var(--shadow-card)',
                      border: `1px solid ${theme.colors.accentPrimary}30`,
                    }
                  : { border: '1px solid transparent' }
                }
                title={`${label}视图`}
              >
                <Icon size={14}/>
                <span>{label}</span>
              </button>
            ))}
          </div>
          </div>

          <button
            type="button"
            onClick={() => onVideoEnabledChange?.(!videoEnabled)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors duration-150 border text-[var(--color-text-secondary)] border-[var(--color-border-primary)] hover:text-[var(--color-accent-primary)] hover:border-[var(--color-accent-primary)]"
            aria-pressed={videoEnabled}
            title={videoEnabled ? "隐藏视频栏；已提交任务继续处理" : "显示视频栏并恢复待处理视频查询"}
          >
            {videoEnabled ? <PanelRightClose size={14} /> : <PanelRightOpen size={14} />}
            <span>{videoEnabled ? "隐藏视频栏" : "显示视频栏"}</span>
          </button>

          <div className="h-6 w-px" style={{ backgroundColor: 'var(--color-border-primary)' }}></div>

          {/* 比例切换按钮组 */}
          <div className="flex items-center gap-1.5">
            <span className="text-2xs font-bold tracking-wide" style={{ color: 'var(--color-text-muted)' }}>比例</span>
            <RatioToggle currentRatio={currentRatio} onChange={handleRatioChange} />
          </div>

          {/* 风格选择下拉 */}
          {/* <StyleDropdown currentStyle={currentStyle} onChange={handleStyleChange} /> */}
        </div>

        {/* 右侧：操作区 */}
        <div className="flex items-center gap-2">
          {/* 镜头数 */}
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md"
            style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
            <SquareStack size={13} style={{ color: 'var(--color-text-muted)'}}/>
            <span className="text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
              {rowsCount}
            </span>
          </div>

          <div className="h-6 w-px" style={{ backgroundColor: 'var(--color-border-primary)' }}></div>

          {/* 多选 */}
          <button
            onClick={() => { if (isMultiSelectMode) clearShotSelection(); else setIsMultiSelectMode(true); }}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors duration-150 border ${isMultiSelectMode ? 'text-[var(--color-accent-contrast)] border-transparent' : 'text-[var(--color-text-muted)] border-[var(--color-border-primary)] hover:text-[var(--color-accent-primary)] hover:border-[var(--color-accent-primary)]'}`}
            style={isMultiSelectMode ? { backgroundColor: theme.colors.accentPrimary } : undefined}
          >
            <CheckSquare size={13} />
            <span>多选</span>
            {selectedShots?.size > 0 && (
              <span className="px-1.5 rounded-full text-2xs font-bold"
                style={{ backgroundColor: isMultiSelectMode ? 'color-mix(in srgb, var(--color-accent-contrast) 25%, transparent)' : `${theme.colors.accentPrimary}20`, color: isMultiSelectMode ? 'var(--color-accent-contrast)' : theme.colors.accentPrimary }}>
                {selectedShots.size}
              </span>
            )}
          </button>
          {isMultiSelectMode && (
            <>
              <button onClick={selectAllShots} className="text-2xs px-2 py-1 rounded-md transition-colors duration-150 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-primary)]"
              >全选</button>
              <button onClick={clearShotSelection} className="text-2xs px-2 py-1 rounded-md transition-colors duration-150 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-primary)]"
              >清空</button>
            </>
          )}

          <div className="h-6 w-px" style={{ backgroundColor: 'var(--color-border-primary)' }}></div>

          {/* 原小说 */}
          {onOpenSourceText && (
            <button
              onClick={onOpenSourceText}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors duration-150 cursor-pointer border text-[var(--color-text-secondary)] border-[var(--color-border-primary)] hover:text-[var(--color-accent-primary)] hover:border-[var(--color-accent-primary)] hover:bg-[color-mix(in_srgb,var(--color-accent-primary)_8%,transparent)]"
              title="打开原小说"
            >
              <FileText size={13} />
              <span className="hidden md:inline">原小说</span>
            </button>
          )}

          <div className="h-6 w-px" style={{ backgroundColor: 'var(--color-border-primary)' }}></div>

          {/* 一键生图 — 主操作 */}
          <button
            onClick={onBatchGenerateImages}
            disabled={isGlobalLocked || rowsCount === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-opacity duration-150 disabled:opacity-40 cursor-pointer bg-[var(--color-accent-primary)] text-[var(--color-accent-contrast)] hover:opacity-90"
            title={isFirstLastFrameMode ? "一键生成首尾帧图片" : "一键生成所有分镜图片"}
          >
            <Wand2 size={13}/>
            <span className="hidden md:inline">{isFirstLastFrameMode ? "生成首尾帧" : "一键生图"}</span>
          </button>
          <EstimatedPoints request={imageCostRequest} compact />

          {/* 组图生成 */}
          {onBatchSequentialImages && (
            <button
              onClick={onBatchSequentialImages}
              disabled={isGlobalLocked || rowsCount < 2}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors duration-150 disabled:opacity-40 cursor-pointer border text-[var(--color-text-secondary)] border-[var(--color-border-primary)] hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-border-hover)]"
              title="选中多个分镜，一次生成角色一致的连续图片（多选或全部）"
            >
              <Layers size={13}/>
              <span className="hidden md:inline">组图生成</span>
            </button>
          )}

          {/* 一键生视频 */}
          {videoEnabled && (
            <>
              <button
                onClick={onBatchGenerateVideos}
                disabled={isGlobalLocked || rowsCount === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors duration-150 disabled:opacity-40 cursor-pointer border text-[var(--color-text-secondary)] border-[var(--color-border-primary)] hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-border-hover)]"
                title={isFirstLastFrameMode ? "一键生成序列视频" : "一键生成所有分镜视频"}
              >
                <Film size={13}/>
                <span className="hidden md:inline">一键生视频</span>
              </button>
              <EstimatedPoints request={videoCostRequest} compact />
            </>
          )}

          {/* 全部扩展到画布9宫格 */}
          {onBatchExpandToCanvas && (
            <button
              onClick={onBatchExpandToCanvas}
              disabled={isGlobalLocked || rowsCount === 0 || isExpandingToCanvas}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors duration-150 disabled:opacity-40 cursor-pointer border text-[var(--color-text-secondary)] border-[var(--color-border-primary)] hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-border-hover)]"
              title="将所有分镜扩展到画布9宫格"
            >
              {isExpandingToCanvas ? (
                <><Loader2 size={13} className="animate-spin"/><span className="hidden md:inline">生成中...</span></>
              ) : (
                <><LayoutGrid size={13}/><span className="hidden md:inline">扩展到画布</span></>
              )}
            </button>
          )}

          {/* 首尾帧序列视频按钮 */}
          {videoEnabled && isFirstLastFrameMode && onBatchGenerateSequenceVideos && (
            <button
              onClick={onBatchGenerateSequenceVideos}
              disabled={isGlobalLocked || rowsCount === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors duration-150 disabled:opacity-40 cursor-pointer border text-[var(--color-text-secondary)] border-[var(--color-border-primary)] hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-border-hover)]"
              title="打开首尾帧序列视频面板"
            >
              <Video size={13}/>
              <span className="hidden md:inline">序列视频</span>
            </button>
          )}

          {/* 一键下载视频 */}
          {videoEnabled && onBatchDownloadVideos && (
            <button
              onClick={onBatchDownloadVideos}
              disabled={isGlobalLocked || rowsCount === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors duration-150 disabled:opacity-40 cursor-pointer border text-[var(--color-text-secondary)] border-[var(--color-border-primary)] hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-border-hover)]"
              title="下载所有分镜视频"
            >
              <Download size={13}/>
              <span className="hidden md:inline">下载视频</span>
            </button>
          )}

          {/* 工业化 */}
          {onShowGridWizard && (
            <button
              onClick={onShowGridWizard}
              className="p-2 rounded-md transition-all duration-200 hover:bg-[var(--color-bg-tertiary)]"
              style={{ color: 'var(--color-text-secondary)' }}
              aria-label="工业化分镜"
              title="工业化分镜"
            >
              <Sparkles size={15} />
            </button>
          )}

          {/* 队列监控 — hover 弹出 */}
          <div className="relative"
            onMouseEnter={() => setShowQueuePopover(true)}
            onMouseLeave={() => setShowQueuePopover(false)}>
            <button
              className="p-2 rounded-md transition-all duration-200 hover:bg-[var(--color-bg-tertiary)]"
              style={{ color: 'var(--color-text-secondary)' }}
              title="队列监控"
            >
              <Activity size={15} />
            </button>
            {queuePopover.mounted && (
              <div className={`absolute top-full right-0 mt-2 w-72 rounded-xl shadow-2xl z-50 overflow-hidden ${queuePopover.exiting
                ? "animate-out fade-out slide-out-to-bottom-2 duration-150"
                : "animate-in fade-in slide-in-from-bottom-2 duration-200"}`}
                style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
                <QueueStatusBar />
                <div className="px-3 py-2 flex justify-end" style={{ borderTop: '1px solid var(--color-border-secondary)' }}>
                  <button onClick={() => { setShowQueuePopover(false); setShowSystemMonitor(true); }}
                    className="text-2xs px-2 py-1 rounded-md transition-colors duration-150 hover:bg-[var(--color-bg-tertiary)]"
                    style={{ color: theme.colors.accentPrimary }}
                  >详细监控</button>
                </div>
              </div>
            )}
          </div>

          {/* 刷新 */}
          <button
            onClick={fetchAssets}
            disabled={isRefreshingAssets}
            className="p-2 rounded-md transition-all hover:bg-[var(--color-bg-tertiary)]"
            style={{ color: 'var(--color-text-secondary)' }}
            aria-label="刷新资源库"
            title="刷新资源库"
          >
            <RefreshCw size={15} className={isRefreshingAssets ? "animate-spin" : ""}/>
          </button>

          {/* 保存 */}
          <button
            onClick={handleSaveAll}
            disabled={isSaving}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-lg transition-opacity duration-150 disabled:opacity-50 cursor-pointer hover:opacity-90"
            style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}
          >
            <Save size={13}/>
            <span>{isSaving ? "保存中" : "保存"}</span>
          </button>
        </div>
      </div>
      </div>

      {/* Batch Progress Bar */}
      {batchAction && (videoEnabled || batchAction !== 'generateVideo') && (
        <div className="h-8 flex items-center px-4 gap-3 text-xs"
          style={{
            backgroundColor: `${theme.colors.accentPrimary}15`,
            borderBottom: '1px solid var(--color-border-primary)'
          }}>
          <span className="font-medium inline-flex items-center gap-1.5" style={{ color: theme.colors.accentPrimary }}>
            {batchAction === 'generateImage' ? <><Palette size={13} />图片生成中</> :
             batchAction === 'generateVideo' ? <><Clapperboard size={13} />视频生成中</> : <><Plus size={13} />批量添加</>}
          </span>
          {batchProgress.total > 0 && (
            <div className="flex items-center gap-2">
              <div className="w-24 h-1.5 rounded-full overflow-hidden"
                style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${(batchProgress.current / batchProgress.total) * 100}%`,
                    backgroundColor: theme.colors.accentPrimary
                  }}
                />
              </div>
              <span style={{ color: 'var(--color-text-secondary)' }}>
                {batchProgress.current}/{batchProgress.total}
              </span>
            </div>
          )}
          <button
            onClick={onCancelBatch}
            className="ml-auto text-xs px-2 py-0.5 rounded hover:bg-[var(--color-bg-tertiary)] transition-all duration-200 cursor-pointer"
            style={{ color: 'var(--color-text-muted)' }}
          >
            取消
          </button>
        </div>
      )}

      {/* 系统监控面板 */}
      <SystemMonitor
        isOpen={showSystemMonitor}
        onClose={() => setShowSystemMonitor(false)}
      />
    </div>
  );
};

export default StoryboardToolbar;
